import { useRef, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast, Notification } from 'components/ui';
import timeoutConfig from 'configs/timeout.config';

/**
 * Hook para gerenciar timeout automático com aviso prévio
 */
export const useAutoLogout = (onLogout, isAuthenticated) => {
  const location = useLocation();
  const autoLogoutTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  // Função para mostrar aviso prévio
  const showWarning = useCallback(() => {
    if (warningShownRef.current) return;

    warningShownRef.current = true;
    setShowWarningDialog(true);

    // Toast adicional para usuários que não veem o dialog
    toast.push(
      <Notification
        closable
        title="Sua sessão expirará em breve"
        type="warning"
        duration={timeoutConfig.WARNING_TOAST_DURATION}
      >
        Você será desconectado em 2 minutos por inatividade. Mova o mouse ou pressione uma
        tecla para manter a sessão ativa.
      </Notification>,
      { placement: 'bottom-end' }
    );
  }, []);

  // Função para executar logout
  const executeLogout = useCallback(() => {
    toast.push(
      <Notification
        closable
        title="Sua sessão expirou"
        type="warning"
        duration={timeoutConfig.LOGOUT_TOAST_DURATION}
      >
        Faça login novamente para continuar.
      </Notification>,
      { placement: 'bottom-end' }
    );
    onLogout();
  }, [onLogout]);

  // Função para verificar se auto-logout deve ser desabilitado
  const shouldDisableAutoLogout = useCallback(() => {
    // Desabilitar auto-logout em páginas de consultas
    return location.pathname.startsWith('/admin/consultas/');
  }, [location.pathname]);

  // Função para configurar os timeouts
  const setupTimeouts = useCallback(() => {
    // Limpar timeouts anteriores
    if (autoLogoutTimeoutRef.current) {
      clearTimeout(autoLogoutTimeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Resetar flag de aviso
    warningShownRef.current = false;

    // Fechar dialog de aviso se estiver aberto
    setShowWarningDialog(false);

    // Não configurar timeouts se estiver em páginas de consultas
    if (shouldDisableAutoLogout()) {
      return;
    }

    // Configurar aviso prévio
    warningTimeoutRef.current = setTimeout(() => {
      showWarning();
    }, timeoutConfig.AUTO_LOGOUT_TIMEOUT - timeoutConfig.WARNING_TIMEOUT);

    // Configurar logout automático
    autoLogoutTimeoutRef.current = setTimeout(() => {
      executeLogout();
    }, timeoutConfig.AUTO_LOGOUT_TIMEOUT);
  }, [showWarning, executeLogout, shouldDisableAutoLogout]);

  // Função para resetar o timer (com debounce)
  const resetTimer = useCallback(
    (eventType) => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;

      // Debounce baseado no tipo de evento
      const debounceDelay =
        eventType === 'mousemove' || eventType === 'mousemove'
          ? timeoutConfig.MOUSE_DEBOUNCE_DELAY
          : timeoutConfig.KEYBOARD_DEBOUNCE_DELAY;

      // Ignorar eventos muito frequentes
      if (timeSinceLastActivity < debounceDelay) {
        return;
      }

      // Limpar timeout de debounce anterior
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Aplicar debounce
      debounceTimeoutRef.current = setTimeout(() => {
        lastActivityRef.current = now;
        setupTimeouts();
      }, debounceDelay);
    },
    [setupTimeouts]
  );

  // Função para estender sessão manualmente
  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    setupTimeouts();

    toast.push(
      <Notification closable title="Sessão estendida" type="success" duration={2000}>
        Sua sessão foi estendida por mais 10 minutos.
      </Notification>,
      { placement: 'bottom-end' }
    );
  }, [setupTimeouts]);

  // Reconfigurar timeouts quando a rota mudar
  useEffect(() => {
    if (!isAuthenticated) return;

    // Reconfigurar timeouts ao mudar de rota
    setupTimeouts();
  }, [location.pathname, isAuthenticated, setupTimeouts]);

  // Configurar listeners de eventos
  useEffect(() => {
    if (!isAuthenticated) return;

    const eventHandlers = {};

    timeoutConfig.ACTIVITY_EVENTS.forEach((eventType) => {
      eventHandlers[eventType] = (event) => {
        // Evitar eventos desnecessários
        if (eventType === 'mousemove' && event.target.tagName === 'SCRIPT') return;
        if (eventType === 'scroll' && event.target === document.body) return;

        resetTimer(eventType);
      };

      document.addEventListener(eventType, eventHandlers[eventType], true);
    });

    // Configurar timeout inicial
    setupTimeouts();

    return () => {
      // Limpar listeners
      timeoutConfig.ACTIVITY_EVENTS.forEach((eventType) => {
        document.removeEventListener(eventType, eventHandlers[eventType], true);
      });

      // Limpar timeouts
      if (autoLogoutTimeoutRef.current) {
        clearTimeout(autoLogoutTimeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [isAuthenticated, resetTimer, setupTimeouts]);

  // Limpar timeouts ao desmontar
  useEffect(() => {
    return () => {
      if (autoLogoutTimeoutRef.current) {
        clearTimeout(autoLogoutTimeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    extendSession,
    resetTimer: () => resetTimer('manual'),
    isWarningShown: warningShownRef.current,
    showWarningDialog,
    setShowWarningDialog,
  };
};

export default useAutoLogout;
